<template>
  <img
    v-if="isPreviewImage"
    :src="previewImage"
    :style="previewImageObjectStyle"
    class="preview-image"
    alt=""
  />
  <div
    v-else
    :class="['info-box', { 'info-box--dragged-over-drop': isDraggedOverDrop }]"
  >
    <div v-if="hasErrors" class="info-box__error">
      <img
        src="@/assets/img/error_x_mark.svg"
        class="info-box__error-image"
        alt="error icon"
      />
      <div
        v-if="errors.extension"
        v-sanitize-html="$t('image.ERROR_EXTENSION')"
        class="info-box__error-message"
      />
      <div v-if="errors.size">
        {{ $t('image.ERROR_SIZE_MESSAGE') }}
        ({{ fileSizeMB }})<br />
        {{ $t('image.ERROR_SIZE_MAX', { n: maxAllowedSize }) }}
      </div>
    </div>

    <button class="info-box__choose-button" @click="chooseImage()">
      <img
        v-if="showImageSelectIcon"
        src="@/assets/img/fileUploader/image_chooser.svg"
        alt=""
        class="info-box__choose-button-icon"
        role="presentation"
      />
      <div class="info-box__choose-button-link">
        {{ $t('image.CHOOSE_IMAGE') }}
      </div>
      <div class="info-box__choose-button-description">
        {{ $t('image.DRAG_DROP') }}
      </div>
    </button>

    <BaseButton
      v-if="allowUnsplash"
      outline
      size="medium"
      @click="openUnsplash()"
    >
      <template #icon>
        <PictureIcon />
      </template>
      {{ $t('image.IMAGE_COLLECTION') }}
    </BaseButton>
  </div>
</template>

<script>
import PictureIcon from '@/components/icon_components/PictureIcon.vue'
import BaseButton from '../base/BaseButton.vue'

export default {
  components: {
    BaseButton,
    PictureIcon,
  },
  props: {
    previewImage: {
      type: String,
      required: false,
      default: '',
    },
    errors: {
      type: Object,
      required: false,
      default: null,
    },
    fileSizeError: {
      type: Number,
      required: true,
    },
    maxAllowedSize: {
      type: String,
      required: true,
    },
    isDraggedOverDrop: {
      type: Boolean,
      required: false,
      default: false,
    },
    showImageSelectIcon: {
      type: Boolean,
      required: false,
      default: true,
    },
    allowUnsplash: {
      type: Boolean,
      required: true,
    },
    previewImageObjectFit: {
      type: String,
      required: true,
    },
    previewImageObjectPosition: {
      type: String,
      required: true,
    },
  },
  emits: ['onOpenFileExplorer', 'onOpenUnsplash'],
  computed: {
    isPreviewImage() {
      return this.previewImage && !this.hasErrors
    },
    fileSizeMB() {
      return `${(this.fileSizeError / 1000000).toFixed(2)} MB`
    },
    hasErrors() {
      return this.errors !== null
    },
    previewImageObjectStyle() {
      return `object-fit: ${this.previewImageObjectFit};
      object-position: ${this.previewImageObjectPosition}`
    },
  },
  methods: {
    chooseImage() {
      this.$emit('onOpenFileExplorer')
    },
    openUnsplash() {
      this.$emit('onOpenUnsplash')
    },
  },
}
</script>

<style lang="scss" scoped>
.preview-image {
  display: flex;
  width: 100%;
  height: 100%;
}

.info-box {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $spacing-m;
  height: 100%;

  @media screen and (max-width: $medium-desktop-width-max) {
    gap: $spacing-3xs;
  }

  &__error {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $spacing-3xs;
    padding: $spacing-4xs;
    width: 100%;
    font-size: $font-size-s;
    background: $color-red-05;
    border-radius: $border-radius-l $border-radius-l 0 0;
    border-bottom: 1px solid $color-border;
    color: $color-error;
  }

  &__choose-button {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: none;
    border-radius: $border-radius-l;
    padding: $spacing-3xs;
    border: 1px solid transparent;
    cursor: pointer;

    &:hover {
      border-color: $color-navigation;
    }

    &:active {
      background: $color-blue-10;
    }
  }

  &__choose-button-icon {
    width: $spacing-3xl;
    height: auto;
    margin-bottom: $spacing-4xs;
  }

  &__choose-button-link {
    @include subtitle-2;
    color: $color-link;
    margin-bottom: $spacing-4xs;
  }

  &__choose-button-description {
    color: $color-text-light;
  }
}

.info-box--dragged-over-drop {
  .info-box__choose-button-icon {
    animation: wiggle 800ms forwards;
  }
}

@keyframes wiggle {
  0% {
    transform: rotate(0deg) scale(1);
  }
  30% {
    transform: rotate(5deg) scale(1.2);
  }
  70% {
    transform: rotate(-2deg) scale(1.2);
  }
  100% {
    transform: rotate(0deg) scale(1.2);
  }
}
</style>
